import { ReactNode, useState } from 'react';
import {
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import {
  PortalTeamBadgeTone,
  PortalTeamChannel,
  PortalTeamData,
  PortalTeamMember,
  PortalTeamOtherMember,
  PortalTeamRaciItem,
} from './portalTeamMock';

const badgeClass: Record<PortalTeamBadgeTone, string> = {
  purple: 'bg-violet-50 text-violet-700',
  blue: 'bg-primary-50 text-primary-700',
  green: 'bg-emerald-50 text-emerald-700',
  orange: 'bg-orange-50 text-orange-700',
  red: 'bg-red-50 text-red-700',
  sky: 'bg-sky-50 text-sky-700',
  amber: 'bg-amber-50 text-amber-800',
};

const raciToneClass: Record<PortalTeamRaciItem['tone'], string> = {
  green: 'border-emerald-200 bg-emerald-50',
  blue: 'border-primary-200 bg-primary-50',
  amber: 'border-amber-200 bg-amber-50',
  purple: 'border-violet-200 bg-violet-50',
};

const raciTitleClass: Record<PortalTeamRaciItem['tone'], string> = {
  green: 'text-emerald-800',
  blue: 'text-primary-800',
  amber: 'text-amber-900',
  purple: 'text-violet-800',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

type PortalProjectTeamProps = {
  team: PortalTeamData;
};

export function PortalProjectTeam({ team }: PortalProjectTeamProps) {
  const [checkpoints, setCheckpoints] = useState(team.checkpoints);
  const [copiedChannelId, setCopiedChannelId] = useState<string | null>(null);

  function toggleCheckpoint(checkpointId: string) {
    setCheckpoints((current) =>
      current.map((item) => (item.id === checkpointId ? { ...item, done: !item.done } : item)),
    );
  }

  async function copyChannel(channel: PortalTeamChannel) {
    try {
      await navigator.clipboard.writeText(channel.value);
      setCopiedChannelId(channel.id);
      window.setTimeout(() => setCopiedChannelId((current) => (current === channel.id ? null : current)), 1500);
    } catch {
      // Pré-visualização sem permissão de clipboard.
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-secondary-900">Pessoas do projeto</h2>
        <p className="mt-1 max-w-2xl text-sm text-secondary-600">
          Conheça os profissionais envolvidos e seus papéis no projeto.
        </p>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5">
          <section className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Equipe Baruk</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {team.barukMembers.map((member) => (
                <MemberCard key={member.id} member={member} variant="baruk" />
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Equipe do cliente</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {team.clientMembers.map((member) => (
                <MemberCard key={member.id} member={member} variant="client" />
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Matriz de responsabilidades</h3>
            <p className="mt-1 text-sm text-secondary-500">
              Visão simplificada de quem decide, executa, é consultado e é informado.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {team.raci.map((item) => (
                <article
                  key={item.id}
                  className={`rounded-2xl border px-4 py-4 ${raciToneClass[item.tone]}`}
                >
                  <p className={`text-sm font-semibold ${raciTitleClass[item.tone]}`}>{item.question}</p>
                  <p className="mt-2 text-sm font-bold text-secondary-900">{item.answer}</p>
                  <p className="mt-1 text-xs text-secondary-600">{item.detail}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Canais de comunicação</h3>
            <ul className="mt-3 space-y-3">
              {team.channels.map((channel) => (
                <li key={channel.id} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                    <ChannelIcon kind={channel.kind} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-secondary-500">{channel.label}</p>
                    <p className="truncate text-sm font-semibold text-secondary-900">{channel.value}</p>
                  </div>
                  {channel.copyable ? (
                    <button
                      type="button"
                      onClick={() => void copyChannel(channel)}
                      className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
                      title={copiedChannelId === channel.id ? 'Copiado' : 'Copiar'}
                      aria-label={`Copiar ${channel.label}`}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Outros envolvidos</h3>
            <ul className="mt-3 space-y-3">
              {team.otherMembers.map((member) => (
                <OtherMemberRow key={member.id} member={member} />
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-semibold text-secondary-900">Sobre a equipe</h3>
            <p className="mt-1 text-sm text-secondary-500">
              Checkpoints de alinhamento do time no projeto.
            </p>
            <ul className="mt-4 space-y-2">
              {checkpoints.map((checkpoint) => (
                <li key={checkpoint.id}>
                  <button
                    type="button"
                    onClick={() => toggleCheckpoint(checkpoint.id)}
                    className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-secondary-50"
                    aria-pressed={checkpoint.done}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        checkpoint.done
                          ? 'border-emerald-500 bg-emerald-500 text-white'
                          : 'border-secondary-300 bg-white text-transparent'
                      }`}
                    >
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        checkpoint.done ? 'text-secondary-900' : 'text-secondary-500'
                      }`}
                    >
                      {checkpoint.label}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function MemberCard({
  member,
  variant,
}: {
  member: PortalTeamMember;
  variant: 'baruk' | 'client';
}) {
  return (
    <article className="rounded-2xl border border-secondary-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-700">
          {initials(member.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-secondary-900">{member.name}</p>
          <p className="text-xs text-secondary-500">{member.role}</p>
          <span
            className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeClass[member.badgeTone]}`}
          >
            {member.badgeLabel}
          </span>
        </div>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-secondary-600">{member.bio}</p>
      <div className="mt-4 flex items-center gap-2">
        <ContactIconButton href={`mailto:${member.email}`} label={`E-mail de ${member.name}`}>
          <EnvelopeIcon className="h-4 w-4" aria-hidden />
        </ContactIconButton>
        <ContactIconButton href={`mailto:${member.email}?subject=Chat%20do%20projeto`} label={`Chat com ${member.name}`}>
          <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
        </ContactIconButton>
        {member.phone ? (
          <ContactIconButton href={`tel:${member.phone.replace(/\s+/g, '')}`} label={`Telefone de ${member.name}`}>
            <PhoneIcon className="h-4 w-4" aria-hidden />
          </ContactIconButton>
        ) : null}
        {variant === 'client' && member.linkedInUrl ? (
          <ContactIconButton href={member.linkedInUrl} label={`LinkedIn de ${member.name}`} external>
            <LinkedInGlyph />
          </ContactIconButton>
        ) : null}
      </div>
    </article>
  );
}

function OtherMemberRow({ member }: { member: PortalTeamOtherMember }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-semibold text-secondary-700">
        {initials(member.name)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-secondary-900">{member.name}</p>
        <p className="truncate text-xs text-secondary-500">{member.role}</p>
      </div>
      <ContactIconButton href={`mailto:${member.email}`} label={`E-mail de ${member.name}`}>
        <EnvelopeIcon className="h-4 w-4" aria-hidden />
      </ContactIconButton>
      <ContactIconButton href={`mailto:${member.email}?subject=Chat%20do%20projeto`} label={`Chat com ${member.name}`}>
        <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />
      </ContactIconButton>
    </li>
  );
}

function ContactIconButton({
  href,
  label,
  children,
  external,
}: {
  href: string;
  label: string;
  children: ReactNode;
  external?: boolean;
}) {
  return (
    <a
      href={href}
      aria-label={label}
      title={label}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-secondary-200 text-secondary-500 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
    >
      {children}
    </a>
  );
}

function ChannelIcon({ kind }: { kind: PortalTeamChannel['kind'] }) {
  if (kind === 'email') return <EnvelopeIcon className="h-4 w-4" aria-hidden />;
  if (kind === 'chat') return <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden />;
  if (kind === 'meetings') return <CalendarDaysIcon className="h-4 w-4" aria-hidden />;
  return <DocumentTextIcon className="h-4 w-4" aria-hidden />;
}

function LinkedInGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
      <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zM8.5 8.5h3.8v2h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V23h-4v-6.6c0-1.57-.03-3.6-2.2-3.6-2.2 0-2.54 1.72-2.54 3.49V23h-4V8.5z" />
    </svg>
  );
}
